import { Button, ButtonDropdown, Modal, Table, Input, InputArea } from '@bitcine/cinesend-theme'
import { useQuery } from '@tanstack/react-query'
import useStore from '../../hooks/use_store'
import { useState } from 'react'

const ImageSpecs = ({ airline }) => {
  const { data: imageSpecs, error, isLoading } = useQuery(['airline-image-specs', `?airline_id=${airline.id}`])
  const { createModel, updateModel, deleteModel } = useStore()
  const [showEditModalForSpec, setShowEditModalForSpec] = useState(null)

  const AIRLINE_IMAGE_SPEC_TYPE = [
    'seriesPoster',
    'seriesHero',
    'seriesFeatureNarrow',
    'seriesFeatureWide',
    'episodePoster',
    'moviePoster',
    'movieHero',
    'movieFeatureNarrow',
    'movieFeatureWide',
    'audioCover',
  ]

  const createImageSpec = (type) => {
    createModel.mutate({
      endpoint: 'airline-image-specs',
      data: {
        airline_id: airline.id,
        type,
      },
    })
  }

  const updateImageSpec = (spec) => {
    updateModel.mutate(
      {
        endpoint: `airline-image-specs`,
        id: spec.id,
        data: {
          width: spec.width,
          height: spec.height,
          thumbnail_spec: spec.thumbnail_spec,
          variant_name: spec.variant_name,
        },
      },
      {
        onSuccess: () => setShowEditModalForSpec(null),
      }
    )
  }

  const deleteImageSpec = (spec) => {
    deleteModel.mutate({ endpoint: 'airline-image-specs', id: spec.id })
  }

  const existingTypes = imageSpecs?.map((spec) => spec.type) ?? []

  return (
    <>
      <Table
        status={{
          pending: isLoading,
          error: error,
          errorMessage: error?.message,
        }}
        widths={[160, 160, 120, 120, 55]}
        header={{
          columns: [
            { text: 'Type', key: 'type' },
            { text: 'Width', key: 'width' },
            { text: 'Height', key: 'height' },
            { text: 'Variant Name', key: 'variant_name' },
            { text: '', key: '' },
          ],
          customElement:
            AIRLINE_IMAGE_SPEC_TYPE.length !== existingTypes.length ? (
              <ButtonDropdown
                button={{
                  text: 'Add Image Spec',
                  icon: 'add',
                }}
                dropdown={{
                  content: AIRLINE_IMAGE_SPEC_TYPE.filter((type) => !existingTypes.includes(type)).map((type) => ({
                    text: type,
                    onClick: () => createImageSpec(type),
                  })),
                }}
              />
            ) : (
              <Button disabled={true} icon={'add'}>
                Add Image Spec
              </Button>
            ),
        }}
        body={{
          data: imageSpecs,
          row: {
            compact: true,
            spaced: true,
            onClick: (event, data) => setShowEditModalForSpec(data),
            render: [
              (data) => data.type,
              (data) => data.width,
              (data) => data.height,
              (data) => data.variant_name,
              (data) => (
                <ButtonDropdown
                  kebab
                  dropdown={{
                    content: [
                      {
                        text: 'Edit',
                        onClick: () => {
                          setShowEditModalForSpec(data)
                        },
                      },
                      {
                        text: 'Delete',
                        className: 'text-red-500',
                        onClick: () => {
                          deleteImageSpec(data)
                        },
                      },
                    ],
                  }}
                />
              ),
            ],
          },
          empty: {
            title: 'No image specs!',
            text: 'Add one to begin.',
            icon: 'image',
          },
        }}
      />
      {showEditModalForSpec && (
        <Modal
          header={'Edit Image Spec'}
          confirmButton={{
            text: 'Save',
            onClick: () => {
              updateImageSpec(showEditModalForSpec)
            },
          }}
          onClose={() => setShowEditModalForSpec(null)}
        >
          <div className='space-y-4'>
            <Input label={'Type'} value={showEditModalForSpec.type} disabled={true} />
            <Input
              label={'Variant Name'}
              value={showEditModalForSpec.variant_name ?? ''}
              onChange={(e) =>
                setShowEditModalForSpec({
                  ...showEditModalForSpec,
                  variant_name: e.target.value,
                })
              }
            />
            <div className='flex flex-row space-x-4'>
              <Input
                label={'Width'}
                type={'number'}
                value={showEditModalForSpec.width ?? 0}
                onChange={(e) =>
                  setShowEditModalForSpec({
                    ...showEditModalForSpec,
                    width: e.target.value,
                  })
                }
              />
              <Input
                label={'Height'}
                type={'number'}
                value={showEditModalForSpec.height ?? 0}
                onChange={(e) =>
                  setShowEditModalForSpec({
                    ...showEditModalForSpec,
                    height: e.target.value,
                  })
                }
              />
            </div>
            <InputArea
              label={'Thumbnail specs (variant,width,height)'}
              value={showEditModalForSpec.thumbnail_spec ?? ''}
              onChange={(e) =>
                setShowEditModalForSpec({
                  ...showEditModalForSpec,
                  thumbnail_spec: e.target.value,
                })
              }
            />
          </div>
        </Modal>
      )}
    </>
  )
}

export default ImageSpecs
